import { useMemo } from 'react';

import { useFormContext } from 'react-hook-form';

import { DEFAULT_FORMAT, formatDate, isDefined, isNotDefined, isOfType } from '@hsp/utilities';

import { AudienceStatus } from '@t/api/audience/enums/audience-status.enum';

import { useIsAudience, useIsExisting, useSaveStoreSelector } from 'pages/audience/shared/save/hooks';
import { SaveForm } from 'types/pages/audience-builder/shared/save';
import { s } from 'utilities/strings';

export const useGiftAmountConfiguration = () => {
  const { watch } = useFormContext<SaveForm>();
  const isAudience = useIsAudience();
  const isExisting = useIsExisting();

  const saveTo = useSaveStoreSelector(({ saveTo }) => saveTo);

  const readOnly = (isExisting || saveTo) && !isAudience;

  const [giftDateEnd, giftDateStart] = watch(['giftDateEnd', 'giftDateStart']);

  const displayValue: string | undefined = useMemo(() => {
    let label: string;

    if (isDefined(giftDateStart) && isDefined(giftDateEnd)) {
      label = `${formatDate(giftDateStart, DEFAULT_FORMAT)} to ${formatDate(giftDateEnd, DEFAULT_FORMAT)}`;
    } else if (isDefined(giftDateStart) && isNotDefined(giftDateEnd)) {
      label = `after ${formatDate(giftDateStart, DEFAULT_FORMAT)}`;
    } else if (isDefined(giftDateEnd) && isNotDefined(giftDateStart)) {
      label = `before ${formatDate(giftDateEnd, DEFAULT_FORMAT)}`;
    } else {
      label = `${s(['Site.No'])} ${s(['Site.Date'], { casing: 'lowercase', plural: true })} ${s(['Site.Given'], {
        casing: 'lowercase',
      })}`;
    }

    return label;
  }, [giftDateEnd, giftDateStart]);

  const data = useSaveStoreSelector(({ data }) => data);

  let isActive = true;
  let datesChanged = false;

  if (isOfType(data, 'shops') && data.status?.id != AudienceStatus.Active) {
    isActive = false;
    const formGiftDateEnd = watch('giftDateEnd');
    const formGiftDateStart = watch('giftDateStart');
    const { giftDateEnd, giftDateStart } = data;

    if (giftDateEnd != formGiftDateEnd || giftDateStart != formGiftDateStart) {
      datesChanged = true;
    }
  }

  return { datesChanged, displayValue, isActive, readOnly };
};
